class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    const alreadyOpen = this.classList.contains('active');
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    if (alreadyOpen) {
      this.trapDrawerFocus();
      this.querySelector('cart-drawer-items')?.dispatchViewEvent();
      return;
    }

    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener('transitionend', () => this.trapDrawerFocus(), { once: true });

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  trapDrawerFocus() {
    const containerToTrapFocusOn = this.classList.contains('is-empty')
      ? this.querySelector('.drawer__inner-empty')
      : document.getElementById('CartDrawer');
    const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
    if (containerToTrapFocusOn) trapFocus(containerToTrapFocusOn, focusElement);
  }

  beginCartRender() {
    this._cartRenderId = (this._cartRenderId || 0) + 1;
    return this._cartRenderId;
  }

  applySectionHtml(responseText, requestId) {
    if (requestId != null && requestId !== this._cartRenderId) return Boolean(this.querySelector('cart-drawer-items tbody tr'));

    const html = new DOMParser().parseFromString(responseText, 'text/html');
    const sourceInner = html.querySelector('.drawer__inner');
    const hasItemsInSource = Boolean(sourceInner?.querySelector('cart-drawer-items tbody tr'));
    const hasItemsNow = Boolean(this.querySelector('cart-drawer-items tbody tr'));

    // Ignore a stale empty section render that would wipe a just-updated drawer.
    if (!hasItemsInSource && hasItemsNow) return true;

    const targetInner = this.querySelector('.drawer__inner');
    if (sourceInner && targetInner) {
      targetInner.replaceWith(sourceInner);
    } else if (sourceInner) {
      const host = this.querySelector('#CartDrawer');
      host?.appendChild(sourceInner);
    }

    if (hasItemsInSource) {
      this.querySelectorAll('.drawer__inner-empty').forEach((el) => el.remove());
    }

    const itemsEl = this.querySelector('cart-drawer-items');
    const hasItems = Boolean(itemsEl?.querySelector('tbody tr'));
    this.classList.toggle('is-empty', !hasItems);
    itemsEl?.classList.toggle('is-empty', !hasItems);

    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));

    if (this.classList.contains('active')) this.trapDrawerFocus();

    return hasItems;
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.productId = parsedState.id;
    const requestId = this.beginCartRender();
    const sectionHtml = parsedState?.sections?.['cart-drawer'];
    const sectionHasItems = Boolean(
      sectionHtml &&
        new DOMParser().parseFromString(sectionHtml, 'text/html').querySelector('cart-drawer-items tbody tr')
    );

    if (sectionHasItems) this.applySectionHtml(sectionHtml, requestId);

    const iconHtml = parsedState?.sections?.['cart-icon-bubble'];
    if (iconHtml) {
      const bubble = document.getElementById('cart-icon-bubble');
      if (bubble) {
        try {
          bubble.innerHTML = this.getSectionInnerHTML(iconHtml);
        } catch (e) {
          console.error(e);
        }
      }
    }

    const finish = () => {
      setTimeout(() => {
        this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
        this.open();
      });
    };

    if (sectionHasItems) {
      finish();
      return;
    }

    const cartUrl = window.routes?.cart_url || '/cart';
    fetch(`${cartUrl}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((text) => {
        this.applySectionHtml(text, requestId);
        finish();
      })
      .catch(() => finish());
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

// Drawer add-on buttons are injected via section HTML after the first add.
// Scripts inside that HTML never run, so bind once on document.
if (!window.__gbiAddonsBound) {
  window.__gbiAddonsBound = true;
  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-gbi-addon-id]');
    if (!btn) return;
    event.preventDefault();
    if (btn.disabled) return;

    const id = btn.getAttribute('data-gbi-addon-id');
    if (!id) return;

    const label = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Adding';

    const drawer = document.querySelector('cart-drawer');
    const restore = () => {
      btn.disabled = false;
      btn.textContent = label;
    };

    fetch(window.routes?.cart_add_url || '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: Number(id), quantity: 1 }],
        sections: ['cart-drawer', 'cart-icon-bubble'],
        sections_url: window.routes?.cart_url || '/cart',
      }),
    })
      .then((res) => res.json())
      .then((state) => {
        if (state?.status) {
          restore();
          return;
        }
        if (drawer && typeof drawer.renderContents === 'function') {
          drawer.renderContents(state);
          return;
        }
        window.location.href = window.routes?.cart_url || '/cart';
      })
      .catch(restore);
  });
}
